import { createFileRoute, Link } from '@tanstack/react-router';
import { LinesChecklist } from '@/components/site/mocks';
import { GAME_MASTERS, GameMasterCard } from '@/components/site/GameMasterCard';
import { Button } from '@/components/ui/button';
import ashfordImage from '@/assets/campaign-ashford.jpg';
import chapelImage from '@/assets/campaign-chapel.jpg';
import beneathImage from '@/assets/campaign-beneath.jpg';

export const Route = createFileRoute('/_site/campaigns')({
  head: () => ({
    meta: [
      { title: 'Campaigns — one world, three stories | Everwake' },
      {
        name: 'description',
        content:
          'Ashford, a young kingdom on older ruins: three campaigns of one, three and ten sessions, each opening where its author set it.',
      },
      { property: 'og:title', content: 'Campaigns — one world, three stories' },
      {
        property: 'og:description',
        content:
          'Ashford in three campaigns: a missing shipment, a bell that stopped ringing, and an old kingdom beneath.',
      },
      { property: 'og:type', content: 'article' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: Campaigns,
});

const campaigns = [
  {
    id: 'ashford',
    title: 'The Kingdom of Ashford',
    sessions: 'One session',
    hook:
      'A missing shipment, and talk on the north road that dies when you walk past. An evening to find out whether this world is yours.',
    image: ashfordImage,
    alt: 'A rain-darkened Ashford road with an overturned cart and lanterns near stone houses',
  },
  {
    id: 'chapel',
    title: 'The Chapel Bell',
    sessions: 'Three sessions',
    hook:
      'The chapel bell has not rung in three mornings, and the rope marks say it was lowered rather than broken. Nobody in the yard wants to be the one who says so.',
    image: chapelImage,
    alt: 'A misty chapel courtyard with a silent bell tower and rope on wet flagstones',
  },
  {
    id: 'beneath',
    title: 'The Old Kingdom Beneath',
    sessions: 'Ten sessions',
    hook:
      'A tremor moves the altar stone, and the draught coming from behind it is older than the kingdom. Ten sessions to decide what should be let out and what should be walled back in.',
    image: beneathImage,
    alt: 'Ancient stone ruins opening beneath a cracked chapel altar in a lantern beam',
  },
];

function Campaigns() {
  return (
    <div className="mx-auto max-w-5xl px-5 py-16">
      <p className="rule-line">Campaigns</p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">
        Ashford — a young kingdom on older ruins
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        Every campaign is a pre-authored world pack: cast, lore, quests, maps and starting points. Three of them share one world, so the road you closed in one is still closed in the next.
      </p>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {campaigns.map((c) => (
          <article key={c.id} className="overflow-hidden rounded-xl border border-border bg-card/60">
            <img
              src={c.image}
              width={1200}
              height={760}
              loading="lazy"
              alt={c.alt}
              className="aspect-[1.58] w-full object-cover"
            />
            <div className="p-6">
              <p className="rule-line">{c.sessions}</p>
              <h2 className="mt-3 font-display text-2xl">{c.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.hook}</p>
              <p className="mt-4 text-xs text-muted-foreground">
                Opens where its author set it.
              </p>
            </div>
          </article>
        ))}
      </div>

      <section className="mt-16 grid gap-8 border-t border-border/70 pt-12 md:grid-cols-[1fr_1fr] md:items-center">
        <div>
          <h2 className="font-display text-3xl">Lines, drawn before you play</h2>
          <p className="mt-4 text-muted-foreground">
            Before each session, a checklist of fifteen content tags lets you shape the boundaries of your evening. The engine builds the story inside the lines you choose from the start.
          </p>
          <p className="mt-3 text-muted-foreground">You can draw another line at any point in a session. A line, once drawn, is never taken back.</p>
        </div>
        <LinesChecklist />
      </section>

      <section className="mt-16 border-t border-border/70 pt-12">
        <h2 className="font-display text-3xl">Two game masters</h2>
        <p className="mt-4 max-w-2xl text-muted-foreground">
          Same world, same dice. Jack pushes; Sophia lingers. You choose which one sits opposite you.
        </p>
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {GAME_MASTERS.map((master) => (
            <GameMasterCard key={master.id} master={master} />
          ))}
        </div>
        <Button asChild variant="link" className="mt-4 px-0">
          <Link to="/game-masters">Hear the difference</Link>
        </Button>
      </section>

      <section className="mt-16 border-t border-border/70 pt-12">
        <h2 className="font-display text-3xl">What is in the box at launch</h2>
        <ul className="mt-4 space-y-2 text-muted-foreground">
          <li>
            Four classes at levels one to three: Champion Fighter, Thief Rogue, Life Cleric, Evoker Wizard.
          </li>
          <li>Two game masters, Jack and Sophia.</li>
          <li>
            Three storyteller presets for the pace you enjoy: tense, calm and unpredictable.
          </li>
          <li>
            Voice or text — push-to-talk by default, captions always on, typing always available.
          </li>
        </ul>
      </section>
    </div>
  );
}
